import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { offerVersionEndpoints, parseApiError } from '@ooh/contracts';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { SectionSkeleton } from '../../layouts/skeletons';
import { OfferVersionSnapshotView } from './offer-version-snapshot-view';
import { SnapshotBackLink } from './snapshot-back-link';

function SnapshotLoading() {
  const { t } = useTranslation('offers');
  return (
    <div className="p-6" role="status">
      <span className="sr-only">{t('workspace.versions.snapshot.loading')}</span>
      <SectionSkeleton />
    </div>
  );
}

function SnapshotNotFound({ offerId }: { offerId: string }) {
  const { t } = useTranslation('offers');

  return (
    <div
      className="flex min-h-80 flex-col items-center justify-center gap-4 px-6 py-10 text-center"
      data-testid="snapshot-not-found"
    >
      <p className="max-w-md text-sm text-muted-foreground">
        {t('workspace.versions.snapshot.notFound')}
      </p>
      <SnapshotBackLink offerId={offerId} label={t('workspace.versions.snapshot.backLink')} />
    </div>
  );
}

function SnapshotForbidden({ offerId }: { offerId: string }) {
  const { t } = useTranslation('offers');

  return (
    <div
      className="flex min-h-80 flex-col items-center justify-center gap-4 px-6 py-10 text-center"
      data-testid="snapshot-forbidden"
    >
      <p className="max-w-md text-sm text-muted-foreground">
        {t('workspace.versions.snapshot.forbidden')}
      </p>
      <SnapshotBackLink offerId={offerId} label={t('workspace.versions.snapshot.backLink')} />
    </div>
  );
}

// ─── Page (snapshot fetch, status screens, page title) ────────

export interface OfferVersionSnapshotPageProps {
  offerId: string;
  versionId: string;
  onOpenUnitDetail: (mediaUnitId: number) => void;
  onTitleChange: (title: string) => void;
}

function useSnapshotPageTitle(title: string, onTitleChange: (title: string) => void) {
  React.useEffect(() => {
    onTitleChange(title);
  }, [title, onTitleChange]);
}

function SnapshotLoadingState({
  onTitleChange,
}: Pick<OfferVersionSnapshotPageProps, 'onTitleChange'>) {
  const { t } = useTranslation('offers');
  useSnapshotPageTitle(t('workspace.versions.snapshot.loading'), onTitleChange);
  return <SnapshotLoading />;
}

function SnapshotErrorState({
  error,
  offerId,
  onTitleChange,
}: Pick<OfferVersionSnapshotPageProps, 'offerId' | 'onTitleChange'> & { error: unknown }) {
  const { t } = useTranslation('offers');
  const isNotFound = parseApiError(error).statusCode === 404;
  useSnapshotPageTitle(
    t(
      isNotFound ? 'workspace.versions.snapshot.notFound' : 'workspace.versions.snapshot.forbidden',
    ),
    onTitleChange,
  );
  return isNotFound ? (
    <SnapshotNotFound offerId={offerId} />
  ) : (
    <SnapshotForbidden offerId={offerId} />
  );
}

function SnapshotReadyState({
  offerId,
  versionId,
  onOpenUnitDetail,
  onTitleChange,
}: OfferVersionSnapshotPageProps) {
  const { t } = useTranslation('offers');
  const { data: snapshot } = useApiSuspenseQuery(offerVersionEndpoints.getSnapshot, {
    id: Number(offerId),
    versionId: Number(versionId),
  });
  useSnapshotPageTitle(
    t('workspace.versions.snapshot.pageTitle', { number: snapshot.versionNumber }),
    onTitleChange,
  );
  return (
    <OfferVersionSnapshotView
      offerId={offerId}
      snapshot={snapshot}
      onOpenUnitDetail={onOpenUnitDetail}
    />
  );
}

export function OfferVersionSnapshotPage(props: OfferVersionSnapshotPageProps) {
  return (
    <QueryBoundary
      fallback={<SnapshotLoadingState onTitleChange={props.onTitleChange} />}
      renderError={(error) => (
        <SnapshotErrorState
          error={error}
          offerId={props.offerId}
          onTitleChange={props.onTitleChange}
        />
      )}
    >
      <SnapshotReadyState {...props} />
    </QueryBoundary>
  );
}
